/**
 * Notification Poller Singleton
 *
 * Módulo singleton que gestiona el intervalo de polling de notificaciones.
 * Sobrevive a reinicios del store Redux y cambios de página.
 *
 * Responsabilidades:
 * - Mantener referencia única al intervalo activo
 * - Limpiar intervalos previos antes de crear nuevos
 * - Proveer API segura para start/stop
 */

class NotificationPoller {
  constructor() {
    this.intervalId = null;
    this.isRunning = false;
    // Default según ambiente - mismo approach que Page.js
    const IS_PRODUCTION = process.env.REACT_APP_IS_PRODUCTION;
    this.pollInterval = IS_PRODUCTION === "true"
      ? 60 * 60 * 1000  // Producción: 1 hora por defecto
      : 2 * 60 * 1000;  // Development: 2 minutos por defecto
  }

  /**
   * Inicia el polling de notificaciones
   * @param {Function} callback - Función a ejecutar en cada intervalo
   * @param {number} interval - Intervalo en milisegundos (opcional)
   * @returns {boolean} - true si se inició correctamente
   */
  start(callback, interval = null) {
    if (typeof callback !== 'function') {
      return false;
    }

    // Limpiar cualquier intervalo previo
    this.stop();

    // Actualizar intervalo si se proporciona
    if (interval && interval > 0) {
      this.pollInterval = interval;
    }

    // Ejecutar inmediatamente
    try {
      callback();
    } catch (error) {
      // Error en ejecución inmediata
    }

    // Configurar intervalo
    this.intervalId = setInterval(() => {
      try {
        callback();
      } catch (error) {
        // Error en intervalo
      }
    }, this.pollInterval);

    this.isRunning = true;

    return true;
  }

  /**
   * Detiene el polling de notificaciones
   * @returns {boolean} - true si se detuvo correctamente
   */
  stop() {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
      this.isRunning = false;
      return true;
    }

    return false;
  }

  /**
   * Verifica si el poller está activo
   * @returns {boolean}
   */
  isActive() {
    return this.isRunning && this.intervalId !== null;
  }

  /**
   * Obtiene el intervalo configurado
   * @returns {number} - Intervalo en milisegundos
   */
  getInterval() {
    return this.pollInterval;
  }

  /**
   * Actualiza el intervalo (requiere restart)
   * @param {number} interval - Nuevo intervalo en milisegundos
   */
  setInterval(interval) {
    if (interval > 0) {
      this.pollInterval = interval;
    }
  }
}

// Exportar instancia singleton
const notificationPoller = new NotificationPoller();

export default notificationPoller;
